import cx from "classnames";
import { ReactNode } from "react";

import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import Close from "atoms/icon/icons/close.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { Modal } from "atoms/modal/Modal";
import { TextButton } from "atoms/text-button/TextButton";

export type ListModalSize = "small" | "medium" | "fullscreen";

type BaseListModalProps = {
  open?: boolean;
  /** Omit to hide the close button and prevent outside-click dismissal. */
  onClose?: () => void;
  title: string;
  size?: ListModalSize;
  children: ReactNode;
  onFirstButtonClick?: () => void;
  /** ReactNode so a caller can stack a second line under the label (e.g. the saving being added). */
  firstButtonLabel?: ReactNode;
  firstButtonDisabled?: boolean;
  onSecondButtonClick?: () => void | undefined;
  secondButtonLabel?: undefined;
  secondButtonDisabled?: boolean;
  titleClassName?: string;
  isRetailProducts?: boolean;
};

type WithSecondButtonProps = {
  onSecondButtonClick: () => void;
  secondButtonLabel: string;
} & Omit<BaseListModalProps, "secondButtonLabel" | "onSecondButtonClick">;

type SelectModalProps = BaseListModalProps | WithSecondButtonProps;

export const ListModal = ({
  open,
  onClose,
  title,
  size,
  children,
  onFirstButtonClick,
  firstButtonLabel,
  firstButtonDisabled = false,
  onSecondButtonClick,
  secondButtonLabel,
  secondButtonDisabled = false,
  titleClassName,
  isRetailProducts = false,
}: SelectModalProps) => {
  const { t } = useTranslation();
  const sizes: { [key: string]: string } = {
    small: "min-h-[360px]",
    medium: "min-h-[460px]",
    fullscreen: "h-[calc(100%_-_env(safe-area-inset-top)_-_12px)] desk:h-auto",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={size && sizes[size]}
      header={
        <div className="flex h-[69px] w-full border-b border-light-grey">
          {onClose && (
            <div className="pl-3.5 pt-3">
              <IconButton
                svg={Close}
                size="small"
                style="iconOnly"
                onClick={onClose}
                aria-label={t("common.close")}
              />
            </div>
          )}
          <div
            className={cx(
              "flex w-full items-center justify-center pb-[24px] pt-[26px] font-sans text-bold2 uppercase tracking-[.04em] text-primary",
              onClose ? "pr-[55px]" : "px-4",
              titleClassName,
            )}
          >
            {title}
          </div>
        </div>
      }
      footer={
        (firstButtonLabel || secondButtonLabel) && (
          <div
            className={cx(
              "flex w-full flex-row items-center gap-2 border-t border-light-grey p-4",
              isRetailProducts && "justify-between",
            )}
          >
            {secondButtonLabel && (
              <div className={cx("flex w-1/2 justify-center pr-1.5", isRetailProducts && "w-auto")}>
                {isRetailProducts ? (
                  <TextButton
                    onClick={onSecondButtonClick}
                    disabled={secondButtonDisabled}
                    className="text-sm font-medium text-primary underline hover:text-primary/80"
                  >
                    {secondButtonLabel}
                  </TextButton>
                ) : (
                  <Button
                    fullWidth
                    style="secondary"
                    onClick={onSecondButtonClick}
                    disabled={secondButtonDisabled}
                  >
                    {secondButtonLabel}
                  </Button>
                )}
              </div>
            )}
            <div className={cx(secondButtonLabel ? "w-1/2" : "w-full")}>
              <Button fullWidth onClick={onFirstButtonClick} disabled={firstButtonDisabled}>
                {firstButtonLabel}
              </Button>
            </div>
          </div>
        )
      }
    >
      {children}
    </Modal>
  );
};
